import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import {
  getVoiceSalesCampaign,
  listVoiceSalesTargets,
  type VoiceSalesTarget,
} from '../../common/api/voiceSales';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { formatOptOutStatus, formatStatusLabel, VoiceSalesStatus } from './presentation';
import { RichContext } from './RichContext';

function statusCode(error: unknown): number {
  return typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status?: unknown }).status ?? 0)
    : 0;
}

function LabelledValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="span">{children}</Box>
    </div>
  );
}

function recordedDate(value: string | null | undefined): string {
  return value ? formatDateStringWithTime(value) : 'Not recorded';
}

function ComplianceContext({ target }: { target: VoiceSalesTarget }) {
  return (
    <SpaceBetween direction="vertical" size="xs">
      <LabelledValue label="Eligibility">
        <VoiceSalesStatus status={target.eligibility_status} />
      </LabelledValue>
      <LabelledValue label="Opt-out">{formatOptOutStatus(target)}</LabelledValue>

      <LabelledValue label="DNC status">
        {target.compliance.dnc_status
          ? formatStatusLabel(target.compliance.dnc_status)
          : 'Not recorded'}
      </LabelledValue>
      <LabelledValue label="DNC scrubbed">
        {recordedDate(target.compliance.dnc_scrubbed_at)}
      </LabelledValue>
      <LabelledValue label="Last checked">
        {recordedDate(target.last_checked_at)}
      </LabelledValue>
      <LabelledValue label="Source state">
        {target.compliance.source_state ?? 'Not recorded'}
      </LabelledValue>
    </SpaceBetween>
  );
}

export default function VoiceSalesCampaignDetailPage() {
  const navigate = useNavigate();
  const { campaignUuid = '' } = useParams({ strict: false }) as { campaignUuid?: string };

  const campaignQuery = useQuery({
    queryKey: ['voice-sales-campaign', campaignUuid],
    queryFn: () => getVoiceSalesCampaign(campaignUuid),
    enabled: Boolean(campaignUuid),
  });
  const targetsQuery = useQuery({
    queryKey: ['voice-sales-targets', campaignQuery.data?.id ?? 'pending'],
    queryFn: () => listVoiceSalesTargets({ campaign_id: campaignQuery.data?.id }),
    enabled: Boolean(campaignQuery.data?.id),
  });

  const campaign = campaignQuery.data;
  const error = campaignQuery.error ?? targetsQuery.error;
  const status = statusCode(error);

  return (
    <DashboardViewLayout
      headerVariant="high-contrast"
      breadcrumbs={[
        { text: 'Voice Sales', href: '/dashboard/voice-sales' },
        { text: 'Campaigns', href: '/dashboard/voice-sales/campaigns' },
        {
          text: campaign?.name ?? 'Campaign details',
          href: campaignUuid ? `/dashboard/voice-sales/campaigns/${campaignUuid}` : '',
        },
      ]}
      header={
        campaign ? (
          <Container
            header={
              <Header
                variant="h1"
                info={<VoiceSalesStatus status={campaign.status} />}
                description="Review campaign setup, audience compliance, and guarded actions."
                actions={
                  <Button
                    iconName="arrow-left"
                    onClick={() => navigate({ to: '/dashboard/voice-sales/campaigns' })}
                  >
                    Back to campaigns
                  </Button>
                }
              >
                {campaign.name}
              </Header>
            }
          >
            <ColumnLayout columns={4} variant="text-grid">
              <LabelledValue label="Audience">Dallas contact discovery</LabelledValue>
              <LabelledValue label="Region">{campaign.source_region}</LabelledValue>
              <LabelledValue label="Daily limit">{campaign.daily_cap} per day</LabelledValue>
              <LabelledValue label="Calling mode">
                {campaign.mode === 'real' ? 'Approved customer calling' : 'Preview only'}
              </LabelledValue>
              <LabelledValue label="Created">
                {formatDateStringWithTime(campaign.created_at)}
              </LabelledValue>
              <LabelledValue label="Updated">
                {formatDateStringWithTime(campaign.updated_at)}
              </LabelledValue>
            </ColumnLayout>
          </Container>
        ) : (
          <Header variant="h1">Campaign details</Header>
        )
      }
    >
      {campaignQuery.isPending ? (
        <Box color="text-body-secondary">Loading campaign details</Box>
      ) : status === 403 ? (
        <Alert type="error" header="You do not have access to Voice Sales.">
          Ask an internal administrator if you believe you need access.
        </Alert>
      ) : status === 404 ? (
        <Alert type="warning" header="Campaign not found">
          This campaign does not exist or is no longer available.
        </Alert>
      ) : campaignQuery.error ? (
        <Alert
          type="error"
          header="Campaign details could not be loaded"
          action={<Button onClick={() => campaignQuery.refetch()}>Retry</Button>}
        >
          Retry to load the campaign.
        </Alert>
      ) : campaign ? (
        <SpaceBetween size="l">
          <Container
            header={
              <Header
                variant="h2"
                counter={`(${targetsQuery.data?.length ?? 0})`}
                description="Eligibility and compliance readback for this campaign."
                actions={
                  <Button iconName="refresh" onClick={() => targetsQuery.refetch()}>
                    Refresh
                  </Button>
                }
              >
                Campaign audience
              </Header>
            }
          >
            <Table
              variant="embedded"
              stripedRows
              stickyHeader
              wrapLines
              loading={targetsQuery.isPending}
              loadingText="Loading campaign targets"
              items={status === 403 ? [] : (targetsQuery.data ?? [])}
              empty={
                <Box textAlign="center">
                  {targetsQuery.error ? (
                    <SpaceBetween size="s">
                      <b>Campaign audience could not be loaded</b>
                      <Button onClick={() => targetsQuery.refetch()}>Retry</Button>
                    </SpaceBetween>
                  ) : (
                    <b>No targets are assigned to this campaign.</b>
                  )}
                </Box>
              }
              columnDefinitions={[
                {
                  id: 'contact',
                  header: 'Contact',
                  cell: (target) => (
                    <RichContext
                      context={target.context}
                      phoneNumber={target.display_phone_number}
                      lineTypeInfo={target.phone_line_type_info}
                      sections={['contact']}
                    />
                  ),
                  minWidth: 240,
                },
                {
                  id: 'business',
                  header: 'Business',
                  cell: (target) => (
                    <RichContext context={target.context} sections={['business']} />
                  ),
                  minWidth: 270,
                },
                {
                  id: 'scorecard',
                  header: 'Scorecard',
                  cell: (target) => (
                    <RichContext context={target.context} sections={['scorecard']} />
                  ),
                  minWidth: 250,
                },
                {
                  id: 'compliance',
                  header: 'Eligibility & compliance',
                  cell: (target) => <ComplianceContext target={target} />,
                  minWidth: 220,
                },
              ]}
            />
          </Container>

          <Alert type="info" header="Launch safeguards remain active">
            Scheduling still enforces eligibility, the 10 per day limit, and the approved test-number override.
          </Alert>
        </SpaceBetween>
      ) : null}
    </DashboardViewLayout>
  );
}
